import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  CERTIFICATIONS_PAGE_SIZE,
  RefreshReactQuery,
} from "../../constants/AppConstants";
import {
  DATA_UPDATED_SUCCESS_LABEL,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
} from "../../constants/Dictionary";
import CmSheetAvailableCertificationsService from "../../services/api/cmSheet/CmSheetAvailableCertifications.service";
import CmSheetCertificationsService from "../../services/api/cmSheet/CmSheetCertifications.service";
import { UpdateCollaboratorsCmSheetCertificationsRequest } from "../../types/api/collaborators.type";
import { useNotification } from "../notification.hook";

export const CM_SHEET_CERTIFICATIONS_QUERY_KEY = "get-cm-sheet-certifications";
export const CM_SHEET_AVAILABLE_CERTIFICATIONS_QUERY_KEY =
  "get-cm-sheet-available-certifications";

export const useCmSheetCertifications = (cmSheetId: number) => {
  const { notification } = useNotification();
  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: [CM_SHEET_CERTIFICATIONS_QUERY_KEY, cmSheetId],
    queryFn: () => CmSheetCertificationsService.list(cmSheetId),
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

export const useUpdateCmSheetCertifications = (cmSheetId: number) => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isUpdating } = useMutation({
    mutationFn: ({
      body,
    }: {
      body: UpdateCollaboratorsCmSheetCertificationsRequest;
      successMessage?: string;
    }) => CmSheetCertificationsService.update(cmSheetId, body),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [CM_SHEET_CERTIFICATIONS_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [CM_SHEET_AVAILABLE_CERTIFICATIONS_QUERY_KEY],
      });
      const message = variables.successMessage ?? DATA_UPDATED_SUCCESS_LABEL;
      notification.success(message, undefined, "toast");
    },
    onError: (error) => {
      console.error(error);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  const updateCmSheetCertifications = (
    body: UpdateCollaboratorsCmSheetCertificationsRequest,
    successMessage?: string,
  ) => mutateAsync({ body, successMessage });

  return { updateCmSheetCertifications, isUpdating };
};

interface UseCmSheetAvailableCertificationsProps {
  cmSheetId: number;
  pageNumber: number;
  selectedCategories: string[];
  searchDebouncedValue: string;
}

export const useCmSheetAvailableCertifications = ({
  cmSheetId,
  pageNumber,
  selectedCategories,
  searchDebouncedValue,
}: UseCmSheetAvailableCertificationsProps) => {
  const { notification } = useNotification();
  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: [
      CM_SHEET_AVAILABLE_CERTIFICATIONS_QUERY_KEY,
      cmSheetId,
      pageNumber,
      selectedCategories,
      searchDebouncedValue,
    ],
    queryFn: ({ signal }) =>
      CmSheetAvailableCertificationsService.list(
        cmSheetId,
        CERTIFICATIONS_PAGE_SIZE,
        pageNumber,
        [
          ...(selectedCategories
            ? [{ key: "category", value: selectedCategories }]
            : []),
          ...(searchDebouncedValue
            ? [{ key: "search", value: [searchDebouncedValue] }]
            : []),
        ],
        signal,
      ),
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};
